import { FetchResult, NormalizedCacheObject } from 'apollo-boost';

import {
    addAdsMutation,
    addVCards,
    archiveAds,
    deleteAds,
    generateVideoAdditionMutation,
    rejectAutoOrganizationsMutation,
    resumeAds,
    saveAdsMutation,
    saveInternalAdsMutation,
    suspendAds,
    unarchiveAds,
    updateAdPrices,
    updateAdPricesMass,
    updateAdsMutation,
    updateImageSmartCenters
} from '../../graphql/mutations/grid/banners';
import { mapBannersSaveApiResponse } from '../../mappers/banners-edit/api-data-mappers';
import { mapInternalBannersSaveApiResponse } from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import {
    mapBannersToApiAddFormat,
    mapBannersToApiDeleteFormat,
    mapBannersToApiEditFormat,
    mapContentPromotionBannersToApiAddFormat,
    mapContentPromotionBannersToApiEditFormat,
    mapCpmBannersToApiAddFormat,
    mapCpmBannersToApiEditFormat,
    mapInternalBannersToApiAddFormat,
    mapInternalBannersToApiEditFormat,
    mapMcBannerBannersToApiAddFormat,
    mapMcBannerBannersToApiEditFormat,
    mapSmartBannersToApiAddFormat,
    mapSmartBannersToApiEditFormat
} from '../../mappers/banners-edit/state-data-mappers/state-data-mappers';
import { NotSyncedError } from '../../service/Errors';
import {
    BannerBulkActionsResponse,
    BannerId,
    SaveBannersResult,
    SaveInternalBannersResult
} from '../../typings/banners';
import {
    EditedBanner,
    EditedBannerTypeMcBanner,
    EditedBannerTypePerformance,
    EditedContentPromotionBanner,
    EditedCpmBanner
} from '../../typings/bannersEditState';
import { CommonData } from '../../typings/common';
import {
    AddAdsMutation,
    AddVCards,
    ArchiveAds,
    DeleteAds,
    GdAddAdsInput,
    GdAddVcardsInput,
    GdAdsMassActionInput,
    GdGenerateVideoAdditionInput,
    GdUpdateAdPriceInput,
    GdUpdateAdsInput,
    GdUpdateOrganizationInput,
    GdUpdateSmartCentersInput,
    GenerateVideoAddition,
    RejectAutoOrganizations,
    ResumeAds,
    SaveAdsMutation,
    SaveInternalAdsMutation,
    SuspendAds,
    UnarchiveAds,
    UpdateAdsMutation,
    UpdateImageSmartCenters
} from '../../typings/graphql-grid';
import { BannersAction } from '../../typings/grid';
import { EditedInternalBanner } from '../../typings/internalBannersEditState';
import { ImageEditorsState } from '../../typings/state';
import { DNAApolloClient } from '../DNAApolloClient/DNAApolloClient';

export type SaveAdsInput = {
    update: Record<BannerId, EditedBanner>;
    add: Record<BannerId, EditedBanner>;
    updateSmartAds: Record<BannerId, EditedBannerTypePerformance>;
    addSmartAds: Record<BannerId, EditedBannerTypePerformance>;
    addMcBannerAds: Record<BannerId, EditedBannerTypeMcBanner>;
    updateMcBannerAds: Record<BannerId, EditedBannerTypeMcBanner>;
    addCpmAds: Record<BannerId, EditedCpmBanner>;
    updateCpmAds: Record<BannerId, EditedCpmBanner>;
    addContentPromotionAds: Record<BannerId, EditedContentPromotionBanner>;
    updateContentPromotionAds: Record<BannerId, EditedContentPromotionBanner>;
    delete: Record<BannerId, EditedBanner>;
};

export type SaveInternalAdsInput = {
    add: Record<BannerId, EditedInternalBanner>;
    update: Record<BannerId, EditedInternalBanner>;
    delete: Record<BannerId, EditedInternalBanner>;
};

export class BannersMutationExecutor {
    constructor(private client: DNAApolloClient<NormalizedCacheObject>) {}

    public async saveAds(
        input: SaveAdsInput,
        login: string,
        commonData: CommonData,
        imageEditor: ImageEditorsState
    ): Promise<SaveBannersResult> {
        try {
            const saveResult: FetchResult<SaveAdsMutation> = await this.client.awaitMutate<SaveAdsMutation>({
                mutation: saveAdsMutation,
                login,
                variables: {
                    addInput: mapBannersToApiAddFormat(input.add, commonData, imageEditor),
                    updateInput: mapBannersToApiEditFormat(input.update, commonData, imageEditor),

                    updateSmartAds: mapSmartBannersToApiEditFormat(input.updateSmartAds),
                    smartAds: mapSmartBannersToApiAddFormat(input.addSmartAds),

                    addCpmAdsInput: mapCpmBannersToApiAddFormat(input.addCpmAds),
                    updateCpmAdsInput: mapCpmBannersToApiEditFormat(input.updateCpmAds),

                    addMcBannerInput: mapMcBannerBannersToApiAddFormat(input.addMcBannerAds, imageEditor),
                    updateMcBannerInput: mapMcBannerBannersToApiEditFormat(input.updateMcBannerAds, imageEditor),

                    addContentPromotionAdsInput: mapContentPromotionBannersToApiAddFormat(input.addContentPromotionAds),
                    updateContentPromotionAdsInput: mapContentPromotionBannersToApiEditFormat(
                        input.updateContentPromotionAds
                    ),

                    deleteInput: mapBannersToApiDeleteFormat<EditedBanner>(input.delete),

                    login
                }
            });

            if (!saveResult.data) {
                throw new Error('No result data');
            }

            return mapBannersSaveApiResponse(saveResult.data, input);
        } catch (error) {
            if (error instanceof NotSyncedError) {
                return mapBannersSaveApiResponse(error.data.data, input);
            }
            throw error;
        }
    }

    public async saveInternalAds(
        input: SaveInternalAdsInput,
        login: string,
        commonData: CommonData
    ): Promise<SaveInternalBannersResult> {
        try {
            const saveResult: FetchResult<SaveInternalAdsMutation> = await this.client.awaitMutate<
                SaveInternalAdsMutation
            >({
                mutation: saveInternalAdsMutation,
                login,
                variables: {
                    updateInput: mapInternalBannersToApiEditFormat(input.update),
                    addInput: mapInternalBannersToApiAddFormat(input.add),
                    deleteInput: mapBannersToApiDeleteFormat<EditedInternalBanner>(input.delete),
                    login
                }
            });

            if (!saveResult.data) {
                throw new Error('No result data');
            }

            return mapInternalBannersSaveApiResponse(
                saveResult.data.updateInternalAds,
                saveResult.data.addInternalAds,
                saveResult.data.deleteAds,
                input
            );
        } catch (error) {
            if (error instanceof NotSyncedError) {
                return mapInternalBannersSaveApiResponse(
                    error.data.data.updateAds,
                    error.data.data.addAds,
                    error.data.data.deleteAds,
                    input
                );
            }
            throw error;
        }
    }

    public async updateAds(input: GdUpdateAdsInput, login: string): Promise<FetchResult<UpdateAdsMutation>> {
        return await this.client.awaitMutate<UpdateAdsMutation>({
            mutation: updateAdsMutation,
            login,
            variables: {
                input,
                login
            }
        });
    }

    public async addAds(input: GdAddAdsInput, login: string): Promise<FetchResult<AddAdsMutation>> {
        return await this.client.awaitMutate<AddAdsMutation>({
            mutation: addAdsMutation,
            login,
            variables: {
                input,
                login
            }
        });
    }

    public runMassAction(
        input: GdAdsMassActionInput,
        login: string,
        action: BannersAction
    ): Promise<BannerBulkActionsResponse> {
        switch (action) {
            case BannersAction.stop: {
                return this.suspendAds(input, login).then(response => response.suspendAds);
            }

            case BannersAction.resume: {
                return this.resumeAds(input, login).then(response => response.resumeAds);
            }

            case BannersAction.archive: {
                return this.archiveAds(input, login).then(response => response.archiveAds);
            }

            case BannersAction.unarchive: {
                return this.unarchiveAds(input, login).then(response => response.unarchiveAds);
            }

            case BannersAction.delete: {
                return this.deleteAds(input, login).then(response => response.deleteAds);
            }

            default: {
                throw Error('Нет обработчика для массового действия над объявлениями');
            }
        }
    }

    public async updateAdPrice(input: GdUpdateAdPriceInput[]) {
        const result = await this.client.mutate({
            mutation: updateAdPrices,
            variables: {
                input
            }
        });

        return result;
    }

    public async updateAdPricesMass(input: GdUpdateAdPriceInput[]) {
        const result = await this.client.mutate({
            mutation: updateAdPricesMass,
            variables: {
                input
            }
        });

        return result;
    }

    public updateImageSmartCenters(input: GdUpdateSmartCentersInput, login: string): Promise<UpdateImageSmartCenters> {
        return this.client
            .awaitMutate<UpdateImageSmartCenters>({
                mutation: updateImageSmartCenters,
                login,
                variables: {
                    input,
                    login
                }
            })
            .then((result: FetchResult<UpdateImageSmartCenters>) => result.data as UpdateImageSmartCenters);
    }

    public async addVCards(input: GdAddVcardsInput): Promise<FetchResult<AddVCards>> {
        return await this.client.mutate<AddVCards>({
            mutation: addVCards,
            variables: {
                input
            }
        });
    }

    public getGeneratedTextVideo(input: GdGenerateVideoAdditionInput): Promise<GenerateVideoAddition> {
        return this.client
            .mutate<GenerateVideoAddition>({
                mutation: generateVideoAdditionMutation,
                variables: {
                    input
                }
            })
            .then((result: FetchResult<GenerateVideoAddition>) => result.data as GenerateVideoAddition);
    }

    public rejectAutoOrganizations(
        permalinks: GdUpdateOrganizationInput[],
        login: string
    ): Promise<RejectAutoOrganizations> {
        return this.client
            .awaitMutate<RejectAutoOrganizations>({
                mutation: rejectAutoOrganizationsMutation,
                login,
                variables: {
                    permalinks
                }
            })
            .then((result: FetchResult<RejectAutoOrganizations>) => result.data as RejectAutoOrganizations);
    }

    private suspendAds(input: GdAdsMassActionInput, login: string): Promise<SuspendAds> {
        return this.client
            .awaitMutate<SuspendAds>({
                mutation: suspendAds,
                login,
                variables: {
                    input
                }
            })
            .then((result: FetchResult<SuspendAds>) => result.data as SuspendAds);
    }

    private resumeAds(input: GdAdsMassActionInput, login: string): Promise<ResumeAds> {
        return this.client
            .awaitMutate<ResumeAds>({
                mutation: resumeAds,
                login,
                variables: {
                    input
                }
            })
            .then((result: FetchResult<ResumeAds>) => result.data as ResumeAds);
    }

    private unarchiveAds(input: GdAdsMassActionInput, login: string): Promise<UnarchiveAds> {
        return this.client
            .awaitMutate<UnarchiveAds>({
                mutation: unarchiveAds,
                login,
                variables: {
                    input
                }
            })
            .then((result: FetchResult<UnarchiveAds>) => result.data as UnarchiveAds);
    }

    private archiveAds(input: GdAdsMassActionInput, login: string): Promise<ArchiveAds> {
        return this.client
            .awaitMutate<ArchiveAds>({
                mutation: archiveAds,
                login,
                variables: {
                    input
                }
            })
            .then((result: FetchResult<ArchiveAds>) => result.data as ArchiveAds);
    }

    private deleteAds(input: GdAdsMassActionInput, login: string): Promise<DeleteAds> {
        return this.client
            .awaitMutate<DeleteAds>({
                mutation: deleteAds,
                login,
                variables: {
                    input
                }
            })
            .then((result: FetchResult<DeleteAds>) => result.data as DeleteAds);
    }
}
